import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase.js';

// Loads all projects, keeps them in sync via realtime subscription, and
// exposes an updateProject(id, patch) function with optimistic updates.
//
// Sort: kanban_column (custom order) then sort_order then name.
const COLUMN_ORDER = { now: 0, next: 1, later: 2, done: 3 };

function sortProjects(list) {
  return [...list].sort((a, b) => {
    const colDiff = (COLUMN_ORDER[a.kanban_column] ?? 99) - (COLUMN_ORDER[b.kanban_column] ?? 99);
    if (colDiff !== 0) return colDiff;
    const sortDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
    if (sortDiff !== 0) return sortDiff;
    return (a.name || '').localeCompare(b.name || '');
  });
}

export function useProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      const { data } = await supabase.from('projects').select('*');
      if (!mounted) return;
      setProjects(sortProjects(data ?? []));
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel('projects-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'projects' },
        (payload) => {
          setProjects((prev) => {
            let next;
            if (payload.eventType === 'DELETE') {
              next = prev.filter((p) => p.id !== payload.old.id);
            } else if (payload.eventType === 'INSERT') {
              if (prev.some((p) => p.id === payload.new.id)) return prev;
              next = [...prev, payload.new];
            } else {
              next = prev.map((p) => (p.id === payload.new.id ? payload.new : p));
            }
            return sortProjects(next);
          });
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  const updateProject = useCallback(async (id, patch) => {
    // Optimistic
    setProjects((prev) =>
      sortProjects(prev.map((p) => (p.id === id ? { ...p, ...patch } : p)))
    );
    const { error } = await supabase.from('projects').update(patch).eq('id', id);
    if (error) {
      console.error('updateProject failed', error);
      // Realtime will eventually correct us on remote-truth differences.
    }
  }, []);

  return { projects, loading, updateProject };
}

export { COLUMN_ORDER };
